import { getLocalStorage, saveLocalStorage, useAuth } from "@elit-hair/elit-tools";
import { useCallback, useEffect, useState } from "react";

type SetValue<T> = (value: T | ((prevValue: T) => T)) => void;

/**
 * Returns a localStorage key scoped to the current user so each user has their own stored data.
 * When not logged in, uses a fallback suffix so the key is still valid.
 */
function getUserScopedKey(baseKey: string, userId: string): string {
    const suffix = userId || "guest";
    return `${baseKey}-${suffix}`;
}

/**
 * Like useLocalStorage but keys are scoped by current user id.
 * When the user changes (e.g. after logout/login), state is re-synced from the new user's storage.
 */
export function useUserScopedLocalStorage<T>(baseKey: string, defaultValue: T): [T, SetValue<T>] {
    const { userData } = useAuth();
    const userId = userData?.id ?? "";
    const key = getUserScopedKey(baseKey, userId);

    const [storedValue, setStoredValue] = useState<T>(() => getLocalStorage(key, defaultValue));

    // Re-read from storage when the key changes (e.g. different user logged in)
    useEffect(() => {
        setStoredValue(getLocalStorage(key, defaultValue));
    }, [key]);

    const setValue: SetValue<T> = useCallback(
        (value) => {
            try {
                setStoredValue((prev) => {
                    const newValue = value instanceof Function ? value(prev) : value;
                    saveLocalStorage(key, newValue);
                    return newValue;
                });
            } catch (err) {
                console.error("Error saving to localStorage:", err);
            }
        },
        [key]
    );

    return [storedValue, setValue];
}
